import React, { useState, useEffect, useCallback } from 'react';
import { orders as ordersApi, exchangeRate as fxApi, landedCost as landedCostApi, ncmRates as ncmRatesApi, costItems as costItemsApi } from '../utils/api';
import { ORDER_STATUS_LIST, ORDER_STATUS_META, ORDER_STATUS_PT, fmtBRL, fmtUSD, fmtRMB, fmtPct } from '../utils/helpers';
import { useToast, ToastContainer } from '../hooks/useToast';
import { useT } from '../i18n/I18nContext';
import BulkOrderModal from './BulkOrderModal';
import DeliveryCard from './DeliveryCard';
import { gridLines } from '../utils/sizeGrid';

// Célula de imposto na Nacionalização: valor em R$ com a alíquota (%) aplicada
// logo abaixo. showPct evita mostrar "0%" quando o NCM não tem alíquotas cadastradas.
function taxCell(value, pct, showPct) {
  return (
    <>
      {fmtBRL(value)}
      {showPct && (
        <div style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 400, lineHeight: 1.2 }}>
          {fmtPct(pct)}
        </div>
      )}
    </>
  );
}

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16,
    }}>
      <div style={{
        background:'white', borderRadius:12, padding:28, maxWidth:400, width:'100%',
        boxShadow:'0 20px 60px rgba(0,0,0,.3)',
      }}>
        <div style={{ fontSize:24, marginBottom:12, textAlign:'center' }}>⚠️</div>
        <p style={{ textAlign:'center', fontSize:15, color:'var(--gray-700)', margin:'0 0 24px', lineHeight:1.5 }}>
          {message}
        </p>
        <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
          <button onClick={onCancel}
            style={{ padding:'9px 22px', border:'1px solid var(--gray-200)', borderRadius:8, background:'white', cursor:'pointer', fontSize:14 }}>
            Cancelar
          </button>
          <button onClick={onConfirm}
            style={{ padding:'9px 22px', background:'var(--pantone)', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrderTab({ quotation: q, onRefresh }) {
  const isOrder = q.order_type && q.order_type !== 'quotation';
  const isRMB = q.currency === 'RMB';
  const { toasts, toast } = useToast();
  const t = useT();
  const [confirmDialog, setConfirmDialog] = useState(null); // { message, action }

  // confirm-order modal (bulk)
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmForm, setConfirmForm] = useState({
    locked_exchange_rate: q.locked_exchange_rate || q.exchange_rate || '',
    requires_sample_approval: false,
    item_prices: (q.items || []).map(it => ({
      id: it.id, product: it.product, specification: it.specification,
      quantity: it.quantity || 0,
      negotiated_price: it.negotiated_price ?? it.confirmed_price ?? it.target_price ?? '',
    })),
  });
  const [saving, setSaving] = useState(false);
  const [loadingFx, setLoadingFx] = useState(false);
  // Modal "transformar em pedido Bulk" (a partir de amostra ou cotação)
  const [showBulkModal, setShowBulkModal] = useState(false);

  // extra costs
  const [extraCosts, setExtraCosts] = useState([]);
  const [newCost, setNewCost] = useState({ description: '', amount_usd: '' });
  const [addingCost, setAddingCost] = useState(false);

  // real cost model (bulk orders only)
  const [costForm, setCostForm] = useState({
    cif_pct: q.cif_pct ?? 20,
    trading_fee_pct: q.trading_fee_pct ?? 10,
    trading_fee_fixed_usd: q.trading_fee_fixed_usd ?? 0,
    import_tax_pct: q.import_tax_pct ?? 20,
  });
  const [savingCosts, setSavingCosts] = useState(false);

  // Nacionalização state
  const [nacForm, setNacForm] = useState({
    freight_usd:     q.freight_usd    ?? 0,
    insurance_usd:   q.insurance_usd  ?? 0,
    icms_pct:        q.icms_pct       ?? '',
    afrmm_pct:       q.afrmm_pct      ?? 8,
    freight_paid_by: q.freight_paid_by ?? 'charth',
  });

  // Custos tipados do pedido (despachante, armazenagem, etc.)
  const [costItemsList, setCostItemsList] = useState(q.cost_items || []);
  const [newCostItem, setNewCostItem] = useState({ category: 'despachante', description: '', amount: '', currency: 'BRL' });
  const [addingCostItem, setAddingCostItem] = useState(false);
  const COST_ITEM_LABELS = {
    despachante: 'Despachante', armazenagem: 'Armazenagem', frete_nacional: 'Frete nacional',
    thc: 'THC', siscomex: 'SISCOMEX', seguro: 'Seguro', frete_internacional: 'Frete internacional', outros: 'Outros',
  };
  const [nacBreakdown, setNacBreakdown] = useState(null);
  const [nacLoading, setNacLoading] = useState(false);
  const [savingNac, setSavingNac] = useState(false);
  const [nacAltMap, setNacAltMap] = useState({}); // ncm -> alternatives array
  const [nacAltOpen, setNacAltOpen] = useState(null); // currently open NCM

  const loadNacBreakdown = useCallback(async () => {
    if (!q.order_type || q.order_type === 'quotation') return;
    setNacLoading(true);
    try {
      const data = await landedCostApi.ofQuotation(q.id);
      setNacBreakdown(data);
    } catch {
      toast(t('nacional.error_load'), 'error');
    } finally {
      setNacLoading(false);
    }
  }, [q.id, q.order_type, t, toast]);

  useEffect(() => {
    if (isOrder) {
      ordersApi.getExtraCosts(q.id).then(setExtraCosts).catch(() => {});
      loadNacBreakdown();
      if (costItemsApi?.listForOrder) costItemsApi.listForOrder(q.id).then(setCostItemsList).catch(() => {});
    }
  }, [q.id, isOrder, loadNacBreakdown]);

  async function handleAddCostItem() {
    if (!newCostItem.amount) return toast('Informe o valor', 'error');
    setAddingCostItem(true);
    try {
      await costItemsApi.addForOrder(q.id, {
        category: newCostItem.category, description: newCostItem.description,
        amount: parseFloat(newCostItem.amount), currency: newCostItem.currency,
        exchange_rate: newCostItem.currency === 'BRL' ? 1 : (q.locked_exchange_rate || q.exchange_rate),
      });
      const list = await costItemsApi.listForOrder(q.id);
      setCostItemsList(list);
      setNewCostItem({ category: 'despachante', description: '', amount: '', currency: 'BRL' });
      await loadNacBreakdown();
      onRefresh();
    } catch (e) { toast(e.response?.data?.error || 'Erro', 'error'); }
    finally { setAddingCostItem(false); }
  }

  async function handleDeleteCostItem(id) {
    try {
      await costItemsApi.delete(id);
      setCostItemsList(l => l.filter(x => x.id !== id));
      await loadNacBreakdown();
      onRefresh();
    } catch { toast('Erro ao remover', 'error'); }
  }

  async function handleSaveCosts() {
    setSavingCosts(true);
    try {
      await ordersApi.updateOrderCosts(q.id, costForm);
      toast('Custos atualizados', 'success');
      onRefresh();
    } catch(e) { toast(e.response?.data?.error || 'Erro', 'error'); }
    finally { setSavingCosts(false); }
  }

  async function handleSaveNac() {
    setSavingNac(true);
    try {
      await ordersApi.updateOrderCosts(q.id, {
        freight_usd:     parseFloat(nacForm.freight_usd)   || 0,
        insurance_usd:   parseFloat(nacForm.insurance_usd) || 0,
        icms_pct:        nacForm.icms_pct !== '' ? parseFloat(nacForm.icms_pct) : null,
        afrmm_pct:       parseFloat(nacForm.afrmm_pct)     || 0,
        freight_paid_by: nacForm.freight_paid_by || 'charth',
      });
      await loadNacBreakdown();
      onRefresh();
      toast(t('nacional.save_btn').replace('💾 ', '') + ' OK', 'success');
    } catch(e) {
      toast(e.response?.data?.error || t('nacional.error_save'), 'error');
    } finally {
      setSavingNac(false);
    }
  }

  // NCM editável na própria tabela da Nacionalização, mesmo com o pedido
  // gerado: grava e recalcula os impostos na hora (simulação).
  const [ncmSaving, setNcmSaving] = useState(null);
  async function saveItemNcm(itemId, ncm, currentNcm) {
    const value = String(ncm || '').trim();
    if (!itemId || value === String(currentNcm || '').trim()) return;
    if (value && !/^[\d.\-\s]+$/.test(value)) { toast('NCM deve ter só números (ex.: 6203.42.00)', 'error'); return; }
    setNcmSaving(itemId);
    try {
      const r = await ordersApi.updateItemNcm(q.id, [{ id: itemId, ncm: value }]);
      setNacAltOpen(null);
      await loadNacBreakdown();
      onRefresh();
      const hit = r.items?.[0];
      toast(hit && value && !hit.has_rates ? `NCM ${value} salvo — sem alíquota cadastrada` : `NCM ${value || 'removido'} — impostos recalculados`,
        hit && value && !hit.has_rates ? 'error' : 'success');
    } catch (e) {
      toast(e.response?.data?.error || 'Erro ao salvar NCM', 'error');
    } finally { setNcmSaving(null); }
  }

  async function handleSuggestNcm(ncm) {
    if (nacAltMap[ncm]) {
      setNacAltOpen(nacAltOpen === ncm ? null : ncm);
      return;
    }
    try {
      const alts = await ncmRatesApi.alternatives(ncm);
      setNacAltMap(m => ({ ...m, [ncm]: alts }));
      setNacAltOpen(ncm);
    } catch {
      toast(t('nacional.error_load'), 'error');
    }
  }

  async function loadFx() {
    setLoadingFx(true);
    try {
      const fx = await fxApi.get();
      setConfirmForm(f => ({ ...f, locked_exchange_rate: fx.rate_with_buffer }));
    } catch { toast('Não foi possível buscar o câmbio', 'error'); }
    finally { setLoadingFx(false); }
  }

  async function handleConfirm() {
    if (!confirmForm.locked_exchange_rate) return toast('Informe o câmbio', 'error');
    setSaving(true);
    try {
      await ordersApi.confirmOrder(q.id, {
        locked_exchange_rate: parseFloat(confirmForm.locked_exchange_rate),
        requires_sample_approval: confirmForm.requires_sample_approval ? 1 : 0,
        item_prices: confirmForm.item_prices.map(it => ({
          id: it.id,
          negotiated_price: parseFloat(it.negotiated_price) || 0,
          quantity: it.quantity,
        })),
      });
      toast('Pedido confirmado!', 'success');
      setShowConfirm(false);
      onRefresh();
    } catch(e) { toast(e.response?.data?.error || 'Erro', 'error'); }
    finally { setSaving(false); }
  }

  function handleCreateSample() {
    setConfirmDialog({
      message: `Confirmar "${q.reference}" como PEDIDO DE AMOSTRA?`,
      action: async () => {
        try {
          await ordersApi.createSample(q.id);
          toast('Pedido de amostra criado!', 'success');
          onRefresh();
        } catch(e) { toast(e.response?.data?.error || 'Erro', 'error'); }
      }
    });
  }

  async function handleChangeStatus(newStatus) {
    try {
      const { quotations: qApi } = await import('../utils/api');
      await qApi.setStatus(q.id, newStatus);
      toast(`Status: ${ORDER_STATUS_PT[newStatus] || newStatus}`, 'success');
      onRefresh();
    } catch(e) { toast(e.response?.data?.error || 'Erro', 'error'); }
  }

  // Preço/quantidade do bulk que ficou "a cotar" — preenchido quando o
  // fornecedor responde, direto na tabela de itens do pedido.
  async function handleSaveItemPrice(item, { price, quantity, quote_bulk_price }) {
    try {
      await ordersApi.updateItemPrices(q.id, [{
        id: item.id,
        price: price !== undefined ? price : undefined,
        quantity: quantity !== undefined ? quantity : undefined,
        quote_bulk_price,
      }]);
      toast(t('bulk.price_saved'), 'success');
      onRefresh();
    } catch (e) { toast(e.response?.data?.error || t('common.error_saving'), 'error'); }
  }

  async function handleUpdateRate() {
    try {
      const fx = await fxApi.get();
      if (!fx?.rate_with_buffer) return toast('Não foi possível buscar câmbio', 'error');
      await ordersApi.updateExchangeRate(q.id, {
        exchange_rate: fx.rate_with_buffer,
        rmb_rate: fx.rmb_per_usd || q.rmb_rate,
      });
      toast('Câmbio atualizado!', 'success');
      onRefresh();
    } catch(e) {
      toast('Erro ao atualizar câmbio', 'error');
    }
  }

  async function handleAddExtraCost() {
    if (!newCost.description) return toast('Informe a descrição', 'error');
    setAddingCost(true);
    try {
      const created = await ordersApi.addExtraCost(q.id, { description: newCost.description, amount_usd: parseFloat(newCost.amount_usd)||0 });
      setExtraCosts(c => [...c, created]);
      setNewCost({ description: '', amount_usd: '' });
    } catch { toast('Erro ao adicionar custo', 'error'); }
    finally { setAddingCost(false); }
  }

  async function handleDeleteExtraCost(id) {
    try {
      await ordersApi.deleteExtraCost(id);
      setExtraCosts(c => c.filter(x => x.id !== id));
    } catch { toast('Erro ao remover custo', 'error'); }
  }

  const multiplier = q.trading_cost_multiplier || q.other_costs_multiplier || 1.6;
  const lockedFx = q.locked_exchange_rate || 0;
  const fob = q.negotiated_price || 0;
  const fobInUsd = isRMB ? fob / (q.rmb_rate || 6.7) : fob;
  const extraTotalUsd = extraCosts.reduce((s, c) => s + (c.amount_usd || 0), 0);
  const totalEstBRL = (fobInUsd + extraTotalUsd) * lockedFx * multiplier;

  if (!isOrder && q.status !== 'Approved') {
    return (
      <div style={{ padding: 24, color: 'var(--gray-400)', textAlign: 'center' }}>
        A aba de Pedido fica disponível quando a cotação for <strong>Aprovada</strong>.
      </div>
    );
  }

  if (!isOrder && q.status === 'Approved') {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Confirmar Pedido</div>
          <div style={{ color: 'var(--gray-400)', marginBottom: 24 }}>
            Cotação aprovada. Confirme como pedido Bulk ou gere um pedido de Amostra.
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => { setShowConfirm(true); loadFx(); }}>
              🔵 Confirmar Pedido Bulk
            </button>
            <button className="btn btn-ghost" onClick={handleCreateSample}>
              🟠 Gerar Pedido de Amostra
            </button>
            <button className="btn btn-ghost" onClick={() => setShowBulkModal(true)}>
              🧾 {t('bulk.open_btn')}
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray-400)' }}>{t('bulk.open_hint')}</div>
        </div>

        {showBulkModal && (
          <BulkOrderModal
            quotation={q}
            toast={toast}
            onClose={() => setShowBulkModal(false)}
            onCreated={result => { setShowBulkModal(false); window.location.href = `/orders/${result.id}`; }}
          />
        )}

        {showConfirm && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 640 }}>
              <div className="modal-header">
                <div className="modal-title">🔵 Confirmar Pedido Bulk</div>
                <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setShowConfirm(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Câmbio a travar (USD → BRL) *</label>
                    <input type="number" step="0.0001" value={confirmForm.locked_exchange_rate}
                      onChange={e => setConfirmForm(f => ({ ...f, locked_exchange_rate: e.target.value }))} />
                    <span className="form-hint">
                      {loadingFx ? 'Buscando...' : 'Taxa atual + 5% de segurança'}
                      {' · '}
                      <button type="button" style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 12, padding: 0 }}
                        onClick={loadFx} disabled={loadingFx}>Atualizar</button>
                    </span>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={confirmForm.requires_sample_approval}
                        onChange={e => setConfirmForm(f => ({ ...f, requires_sample_approval: e.target.checked }))} />
                      Requer aprovação de amostra antes de produção
                    </label>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Valor FOB Negociado por Item ({q.currency})</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--gray-100)' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Produto</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Qtd</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>FOB Unit ({q.currency})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {confirmForm.item_prices.map((it, idx) => (
                        <tr key={it.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                          <td style={{ padding: '6px 8px' }}>{it.product}{it.specification ? ` — ${it.specification}` : ''}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{it.quantity}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <input type="number" step="0.01" min="0"
                              value={it.negotiated_price}
                              onChange={e => setConfirmForm(f => ({
                                ...f,
                                item_prices: f.item_prices.map((x, i) => i === idx ? { ...x, negotiated_price: e.target.value } : x),
                              }))}
                              style={{ width: 100, textAlign: 'right' }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setShowConfirm(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleConfirm} disabled={saving}>
                  {saving ? 'Confirmando...' : '✅ Confirmar Pedido'}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmDialog && (
          <ConfirmModal
            message={confirmDialog.message}
            onConfirm={async () => { await confirmDialog.action(); setConfirmDialog(null); }}
            onCancel={() => setConfirmDialog(null)}
          />
        )}
      </div>
    );
  }

  const currentIdx = ORDER_STATUS_LIST.indexOf(q.status);
  const visibleStatuses = ORDER_STATUS_LIST.filter(s => {
    if (!q.requires_sample_approval && q.order_type === 'bulk' && ['Sample Sent','Sample Approved'].includes(s)) return false;
    return true;
  });
  const visibleIdx = visibleStatuses.indexOf(q.status);
  const prevStatus = visibleIdx > 0 ? visibleStatuses[visibleIdx - 1] : null;
  const nextStatus = visibleIdx < visibleStatuses.length - 1 ? visibleStatuses[visibleIdx + 1] : null;

  return (
    <div style={{ padding: 24 }}>
      {/* Status pipeline */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 24, paddingBottom: 4 }}>
        {visibleStatuses.map(s => {
          const idx = ORDER_STATUS_LIST.indexOf(s);
          const curIdx = ORDER_STATUS_LIST.indexOf(q.status);
          const done = idx < curIdx;
          const current = s === q.status;
          return (
            <div key={s} style={{
              padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: current ? 700 : 400, whiteSpace: 'nowrap',
              background: done ? 'var(--pantone-pale)' : current ? 'var(--pantone)' : 'var(--gray-100)',
              color: current ? 'white' : done ? 'var(--pantone)' : 'var(--gray-400)',
              border: current ? '2px solid var(--pantone)' : '1px solid transparent',
            }}>
              {ORDER_STATUS_META[s]?.emoji} {ORDER_STATUS_PT[s] || s}
            </div>
          );
        })}
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 4 }}>TIPO</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            {q.order_type === 'sample' ? '🟠 Sample' : '🔵 Bulk'}
            {q.requires_sample_approval ? ' + Aprovação Amostra' : ''}
          </div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 4 }}>VALOR NEGOCIADO FOB (TOTAL)</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            {isRMB ? fmtRMB(fob) : fmtUSD(fob)}
          </div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 4 }}>CÂMBIO TRAVADO</div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>R$ {lockedFx.toFixed(4)}</div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 4 }}>
            TOTAL ESTIMADO BRL {q.trading_cost_multiplier ? '(custo real)' : '(estimativa)'}
          </div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--pantone)' }}>{fmtBRL(totalEstBRL)}</div>
        </div>
      </div>

      {/* Live exchange rate indicator */}
      {(() => {
        const payments = q.payments || [];
        return (
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16, padding:'10px 14px', background:'var(--gray-50)', borderRadius:'var(--radius)', border:'1px solid var(--gray-100)' }}>
            <span style={{ fontSize:13, color:'var(--gray-600)' }}>
              💱 Câmbio: <strong>R$ {Number(q.exchange_rate || 5.20).toFixed(4)}</strong>
              {q.updated_at && (
                <span style={{ fontSize:11, color:'var(--gray-400)', marginLeft:6 }}>
                  (atualizado {q.updated_at.slice(0,10)})
                </span>
              )}
            </span>
            {payments.length === 0 ? (
              <button
                onClick={handleUpdateRate}
                style={{ padding:'4px 10px', fontSize:11, border:'1px solid var(--gray-200)', borderRadius:6, cursor:'pointer', background:'#F0FFF4', color:'#276749' }}>
                🔄 Atualizar
              </button>
            ) : (
              <span style={{ fontSize:11, color:'#E65100', background:'#FFF3E0', padding:'2px 8px', borderRadius:10 }}>
                🔒 Travado ({payments.length} pagamento{payments.length>1?'s':''})
              </span>
            )}
          </div>
        );
      })()}

      {/* Prazo de produção e entrega prevista */}
      <DeliveryCard quotation={q} onRefresh={onRefresh} toast={toast} />

      {/* FOB por item — com foto do produto e preenchimento do preço "a cotar" */}
      {q.items?.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8 }}>FOB NEGOCIADO POR ITEM</div>
          <div className="table-wrap" style={{ border: 'none' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--gray-100)' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', width: 50 }}></th>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Produto</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Qtd</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>FOB Unit</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>FOB Total</th>
              </tr>
            </thead>
            <tbody>
              {q.items.map(it => {
                const price = it.negotiated_price || 0;
                const total = price * (it.quantity || 0);
                const photos = (it.files || []).filter(f => f.file_type === 'image');
                const toQuote = !!it.bulk_quote_requested;
                return (
                  <tr key={it.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '6px 8px' }}>
                      {photos.length ? (
                        <a href={`/uploads/${photos[0].filename}`} target="_blank" rel="noreferrer" title={t('downloads.open_photo')}>
                          <img src={`/uploads/${photos[0].filename}`} alt=""
                            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--gray-200)' }} />
                        </a>
                      ) : <span style={{ fontSize: 18, color: 'var(--gray-300)' }}>📦</span>}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {it.product}{it.specification ? ` — ${it.specification}` : ''}
                      {it.is_garment ? gridLines(it).map((line, li) => (
                        <div key={li} style={{ fontSize: 11, color: 'var(--gray-500)', fontFamily: 'var(--font-mono)' }}>{li === 0 ? '👗 ' : ''}{line}</div>
                      )) : null}
                      {photos.length > 1 && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--gray-400)' }}>+{photos.length - 1} 📷</span>
                      )}
                      {toQuote && (
                        <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                          background: 'var(--pantone-pale)', color: 'var(--pantone)' }}>
                          {t('bulk.badge_to_quote')}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      {toQuote ? (
                        <input type="number" min="0" defaultValue={it.quantity || ''} placeholder="qtd"
                          onBlur={e => {
                            const v = parseFloat(e.target.value);
                            if (Number.isNaN(v) || v === (it.quantity || 0)) return;
                            handleSaveItemPrice(it, { quantity: v, quote_bulk_price: true });
                          }}
                          style={{ width: 80, textAlign: 'right', padding: '4px 6px', fontSize: 12 }} />
                      ) : it.quantity}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      {toQuote ? (
                        <input type="number" min="0" step="0.01" placeholder={t('bulk.to_quote_short')}
                          onBlur={e => {
                            const v = parseFloat(e.target.value);
                            if (Number.isNaN(v) || v <= 0) return;
                            handleSaveItemPrice(it, { price: v, quote_bulk_price: false });
                          }}
                          style={{ width: 96, textAlign: 'right', padding: '4px 6px', fontSize: 12 }} />
                      ) : (isRMB ? fmtRMB(price) : fmtUSD(price))}
                      {toQuote && it.initial_price ? (
                        <div style={{ fontSize: 10, color: 'var(--gray-400)' }}>
                          {t('bulk.ref_price')} {isRMB ? fmtRMB(it.initial_price) : fmtUSD(it.initial_price)}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>
                      {toQuote ? <span style={{ fontSize: 11, color: 'var(--pantone)' }}>{t('bulk.to_quote_short')}</span>
                               : (isRMB ? fmtRMB(total) : fmtUSD(total))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {q.items.some(i => i.bulk_quote_requested) && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--gray-500)' }}>
              💡 {t('bulk.fill_price_hint')}
            </div>
          )}
        </div>
      )}

      {/* Navigation buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {prevStatus && (
          <button className="btn btn-ghost" onClick={() => handleChangeStatus(prevStatus)}>
            ← {ORDER_STATUS_PT[prevStatus] || prevStatus}
          </button>
        )}
        {nextStatus && (
          <button className="btn btn-primary" onClick={() => handleChangeStatus(nextStatus)}>
            {ORDER_STATUS_META[nextStatus]?.emoji} Avançar para {ORDER_STATUS_PT[nextStatus] || nextStatus}
          </button>
        )}
      </div>

      {/* Trading cost multiplier */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8 }}>CUSTO REAL DA TRADING</div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 12 }}>Multiplicador real (substitui estimativa {q.other_costs_multiplier || 1.6}×)</label>
          <input type="number" step="0.01" min="1"
            defaultValue={q.trading_cost_multiplier || ''}
            placeholder={`${q.other_costs_multiplier || 1.6} (atual)`}
            onBlur={async e => {
              const v = parseFloat(e.target.value);
              if (!v || v === q.trading_cost_multiplier) return;
              try {
                const { quotations: qApi } = await import('../utils/api');
                await qApi.update(q.id, { ...q, trading_cost_multiplier: v });
                onRefresh();
              } catch {}
            }}
            style={{ width: 140 }}
          />
        </div>
      </div>

      {/* Extra costs */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 12 }}>CUSTOS EXTRAS DE DESENVOLVIMENTO</div>
        {extraCosts.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
            <thead>
              <tr style={{ background: 'var(--gray-100)' }}>
                <th style={{ padding: '5px 8px', textAlign: 'left' }}>Descrição</th>
                <th style={{ padding: '5px 8px', textAlign: 'right' }}>Valor (USD)</th>
                <th style={{ padding: '5px 8px', textAlign: 'right' }}>Valor (BRL est.)</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {extraCosts.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '5px 8px' }}>{c.description}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right' }}>{fmtUSD(c.amount_usd)}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right' }}>{lockedFx ? fmtBRL(c.amount_usd * lockedFx * multiplier) : '—'}</td>
                  <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDeleteExtraCost(c.id)} title="Remover">✕</button>
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: 700, background: 'var(--accent-light)' }}>
                <td style={{ padding: '5px 8px' }}>Total extras</td>
                <td style={{ padding: '5px 8px', textAlign: 'right' }}>{fmtUSD(extraTotalUsd)}</td>
                <td style={{ padding: '5px 8px', textAlign: 'right' }}>{lockedFx ? fmtBRL(extraTotalUsd * lockedFx * multiplier) : '—'}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 180 }}>
            <label style={{ fontSize: 11 }}>Descrição</label>
            <input type="text" value={newCost.description} onChange={e => setNewCost(c => ({ ...c, description: e.target.value }))}
              placeholder="Ex: Custom Mold" />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 100 }}>
            <label style={{ fontSize: 11 }}>Valor (USD)</label>
            <input type="number" step="0.01" min="0" value={newCost.amount_usd}
              onChange={e => setNewCost(c => ({ ...c, amount_usd: e.target.value }))} placeholder="0.00" />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={handleAddExtraCost} disabled={addingCost} style={{ marginBottom: 0 }}>
            + Adicionar
          </button>
        </div>
      </div>

      {/* Real cost model — bulk orders only */}
      {q.order_type === 'bulk' && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 12 }}>CUSTOS REAIS DO PEDIDO</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>CIF % (frete + seguro)</label>
              <input type="number" step="0.5" min="0" max="100" value={costForm.cif_pct}
                onChange={e => setCostForm(f => ({ ...f, cif_pct: parseFloat(e.target.value) || 0 }))} />
              <span className="form-hint">% acima do FOB</span>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Fee Trading %</label>
              <input type="number" step="0.5" min="0" max="100" value={costForm.trading_fee_pct}
                onChange={e => setCostForm(f => ({ ...f, trading_fee_pct: parseFloat(e.target.value) || 0 }))} />
              <span className="form-hint">% do FOB</span>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Fee Trading Fixo (USD)</label>
              <input type="number" step="10" min="0" value={costForm.trading_fee_fixed_usd}
                onChange={e => setCostForm(f => ({ ...f, trading_fee_fixed_usd: parseFloat(e.target.value) || 0 }))} />
              <span className="form-hint">valor fixo por pedido</span>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Imposto Importação %</label>
              <input type="number" step="0.5" min="0" max="100" value={costForm.import_tax_pct}
                onChange={e => setCostForm(f => ({ ...f, import_tax_pct: parseFloat(e.target.value) || 0 }))} />
              <span className="form-hint">% sobre CIF + Trading</span>
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={handleSaveCosts} disabled={savingCosts}>
              {savingCosts ? '⏳ Salvando...' : '💾 Salvar Custos'}
            </button>
          </div>
        </div>
      )}

      {/* Custo efetivo (estimado × efetivo) + container */}
      {isOrder && q.effective && (() => {
        const eff = q.effective;
        const estimativa = (fobInUsd + extraTotalUsd) * (lockedFx || q.exchange_rate || 0) * multiplier;
        const bk = eff.buckets || {};
        const buckets = [
          { label: 'FOB', v: eff.fobBrl },
          { label: 'Frete', v: (bk.frete || 0) + (bk.seguro || 0) },
          { label: 'Impostos (líq.)', v: (eff.taxesGross || 0) - (eff.taxesRecoverable || 0) },
          { label: 'Despachante', v: bk.despachante || 0 },
          { label: 'Armazenagem', v: bk.armazenagem || 0 },
          { label: 'Taxas', v: bk.taxas || 0 },
        ];
        return (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)' }}>CUSTO EFETIVO</div>
              {q.container
                ? <a href={`/containers/${q.container.id}`} style={{ fontSize: 12, color: 'var(--pantone)', fontWeight: 600 }}>🚢 {q.container.code}</a>
                : <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>Não está em nenhum container</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 12 }}>
              <div style={{ padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', border: '1px solid var(--gray-100)' }}>
                <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>ESTIMATIVA ({multiplier}×)</div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{fmtBRL(estimativa)}</div>
              </div>
              <div style={{ padding: '12px 14px', background: 'var(--pantone-pale)', borderRadius: 'var(--radius)', border: '1px solid var(--accent)' }}>
                <div style={{ fontSize: 11, color: 'var(--pantone)' }}>CUSTO EFETIVO</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--pantone)' }}>{fmtBRL(eff.custoEfetivo)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {buckets.map(b => (
                <span key={b.label} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, background: 'var(--gray-100)', color: 'var(--gray-600)' }}>
                  {b.label}: <strong>{fmtBRL(b.v)}</strong>
                </span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Custos tipados do pedido (despachante, armazenagem, frete nacional...) */}
      {isOrder && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 12 }}>CUSTOS DO PEDIDO (despachante, armazenagem, taxas)</div>
          {costItemsList.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
              <thead>
                <tr style={{ background: 'var(--gray-100)' }}>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Categoria</th>
                  <th style={{ padding: '5px 8px', textAlign: 'left' }}>Descrição</th>
                  <th style={{ padding: '5px 8px', textAlign: 'right' }}>Valor (BRL)</th>
                  <th style={{ width: 32 }}></th>
                </tr>
              </thead>
              <tbody>
                {costItemsList.map(ci => (
                  <tr key={ci.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '5px 8px' }}>{COST_ITEM_LABELS[ci.category] || ci.category}</td>
                    <td style={{ padding: '5px 8px', color: 'var(--gray-500)' }}>{ci.description || '—'}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtBRL(ci.amount_brl)}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => handleDeleteCostItem(ci.id)} title="Remover">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 8, alignItems: 'end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Categoria</label>
              <select value={newCostItem.category} onChange={e => setNewCostItem(n => ({ ...n, category: e.target.value }))}>
                {Object.entries(COST_ITEM_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Descrição</label>
              <input value={newCostItem.description} onChange={e => setNewCostItem(n => ({ ...n, description: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Valor</label>
              <input type="number" step="0.01" value={newCostItem.amount} onChange={e => setNewCostItem(n => ({ ...n, amount: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Moeda</label>
              <select value={newCostItem.currency} onChange={e => setNewCostItem(n => ({ ...n, currency: e.target.value }))}>
                <option value="BRL">BRL</option><option value="USD">USD</option><option value="RMB">RMB</option>
              </select>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={handleAddCostItem} disabled={addingCostItem} style={{ marginBottom: 0 }}>+ Adicionar</button>
          </div>
        </div>
      )}

      {/* Nacionalização (impostos + custo efetivo) — todos os pedidos */}
      {isOrder && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 12 }}>
            {t('nacional.title')}
          </div>

          {/* Editable inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>{t('nacional.freight_usd')}</label>
              <input type="number" step="10" min="0"
                value={nacForm.freight_usd}
                onChange={e => setNacForm(f => ({ ...f, freight_usd: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>Frete pago por</label>
              <select value={nacForm.freight_paid_by}
                onChange={e => setNacForm(f => ({ ...f, freight_paid_by: e.target.value }))}>
                <option value="charth">Charth (entra no saldo)</option>
                <option value="fornecedor">Fornecedor (CIF/DDP)</option>
                <option value="agente">Agente</option>
                <option value="forwarder">Forwarder</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>{t('nacional.insurance_usd')}</label>
              <input type="number" step="10" min="0"
                value={nacForm.insurance_usd}
                onChange={e => setNacForm(f => ({ ...f, insurance_usd: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>{t('nacional.icms_pct')}</label>
              <input type="number" step="0.5" min="0" max="99"
                value={nacForm.icms_pct}
                placeholder="padrão"
                onChange={e => setNacForm(f => ({ ...f, icms_pct: e.target.value }))} />
              <span className="form-hint">{t('nacional.icms_hint')}</span>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: 11 }}>{t('nacional.afrmm_pct')}</label>
              <input type="number" step="0.5" min="0" max="100"
                value={nacForm.afrmm_pct}
                onChange={e => setNacForm(f => ({ ...f, afrmm_pct: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={handleSaveNac} disabled={savingNac}>
              {savingNac ? t('nacional.saving_btn') : t('nacional.save_btn')}
            </button>
          </div>

          {/* Breakdown section */}
          {nacLoading && (
            <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 12 }}>⏳ Calculando...</div>
          )}

          {!nacLoading && nacBreakdown && (() => {
            const { totals, items: bkItems } = nacBreakdown;
            if (!bkItems || bkItems.length === 0) {
              return (
                <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 12 }}>
                  {t('nacional.no_items')}
                </div>
              );
            }
            return (
              <>
                {/* Prominent totals */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginTop: 16 }}>
                  <div style={{ padding: '12px 14px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', border: '1px solid var(--gray-100)' }}>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 4 }}>{t('nacional.desembolso')}</div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{fmtBRL(totals.desembolso)}</div>
                  </div>
                  <div style={{ padding: '12px 14px', background: '#F0FFF4', borderRadius: 'var(--radius)', border: '1px solid #C6F6D5' }}>
                    <div style={{ fontSize: 11, color: '#276749', marginBottom: 4 }}>{t('nacional.recuperavel')}</div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#276749' }}>{fmtBRL(totals.recuperavel)}</div>
                  </div>
                  <div style={{ padding: '12px 14px', background: 'var(--pantone-pale)', borderRadius: 'var(--radius)', border: '1px solid var(--accent)' }}>
                    <div style={{ fontSize: 11, color: 'var(--pantone)', marginBottom: 4 }}>{t('nacional.custo_efetivo')}</div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--pantone)' }}>{fmtBRL(totals.custoEfetivo)}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 8, fontStyle: 'italic' }}>
                  {t('nacional.caption')}
                </div>

                {/* Per-item table */}
                <div style={{ overflowX: 'auto', marginTop: 14 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--gray-100)' }}>
                        <th style={{ padding: '5px 8px', textAlign: 'left' }}>Produto</th>
                        <th style={{ padding: '5px 8px', textAlign: 'left' }}>{t('nacional.table.ncm')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('nacional.table.va')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('nacional.table.ii')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('nacional.table.ipi')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('nacional.table.pis')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('nacional.table.cofins')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('nacional.table.icms')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('nacional.table.custo_unit')}</th>
                        <th style={{ padding: '5px 8px', textAlign: 'center' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {bkItems.map((item, idx) => (
                        <React.Fragment key={idx}>
                          <tr style={{ borderBottom: '1px solid var(--gray-100)' }}>
                            <td style={{ padding: '5px 8px' }}>
                              <div style={{ fontWeight: 600 }}>{item.product || `Item ${idx + 1}`}</div>
                              {item.specification && (
                                <div style={{ fontSize: 10, color: 'var(--gray-400)', lineHeight: 1.2 }}>{item.specification}</div>
                              )}
                            </td>
                            <td style={{ padding: '5px 8px', color: item.hasRates ? 'inherit' : 'var(--gray-400)', fontFamily: 'monospace' }}>
                              <input
                                key={`${item.id}-${item.ncm || ''}`}
                                defaultValue={item.ncm || ''}
                                placeholder="0000.00.00"
                                aria-label={`NCM de ${item.product || `item ${idx + 1}`}`}
                                title="Digite o NCM e aperte Enter (ou saia do campo) para recalcular os impostos"
                                disabled={!item.id || ncmSaving === item.id}
                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { e.currentTarget.value = item.ncm || ''; e.currentTarget.blur(); } }}
                                onBlur={e => saveItemNcm(item.id, e.target.value, item.ncm)}
                                style={{ width: 104, padding: '3px 6px', fontSize: 12, fontFamily: 'monospace',
                                  border: `1px solid ${item.ncm && !item.hasRates ? '#F6AD55' : 'var(--gray-200)'}`, borderRadius: 4 }} />
                              {ncmSaving === item.id && <span style={{ marginLeft: 4, fontSize: 10 }}>⏳</span>}
                              {!item.hasRates && item.ncm && (
                                <span style={{ marginLeft: 4, fontSize: 10, color: '#E65100' }}>
                                  ⚠ <a href="/import-config" style={{ color: '#E65100', textDecoration: 'underline' }}>
                                    {t('nacional.no_rates')}
                                  </a>
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{fmtBRL(item.VA)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{taxCell(item.ii, item.ii_pct, item.hasRates)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{taxCell(item.ipi, item.ipi_pct, item.hasRates)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{taxCell(item.pis, item.pis_pct, item.hasRates)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{taxCell(item.cofins, item.cofins_pct, item.hasRates)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{taxCell(item.icms, item.icms_pct, item.icms_pct != null)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--pantone)' }}>
                              {item.qty > 0 ? fmtBRL(item.custoUnit) : '—'}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                              {item.ncm && (
                                <button
                                  className="btn btn-ghost btn-sm"
                                  style={{ fontSize: 10, padding: '2px 6px', whiteSpace: 'nowrap' }}
                                  onClick={() => handleSuggestNcm(item.ncm)}
                                  title={t('nacional.suggest_ncm')}>
                                  💡
                                </button>
                              )}
                            </td>
                          </tr>
                          {/* NCM alternatives panel */}
                          {nacAltOpen === item.ncm && nacAltMap[item.ncm] && (
                            <tr>
                              <td colSpan={10} style={{ padding: '8px 12px', background: '#FFFBEB', borderBottom: '1px solid var(--gray-100)' }}>
                                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{t('nacional.suggest_title')}</div>
                                <div style={{ fontSize: 11, color: '#B45309', marginBottom: 8, fontWeight: 600 }}>
                                  {t('nacional.suggest_warning')}
                                </div>
                                {nacAltMap[item.ncm].length === 0 ? (
                                  <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Nenhuma alternativa encontrada no capítulo.</div>
                                ) : (
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                    <thead>
                                      <tr style={{ background: '#FEF3C7' }}>
                                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>NCM</th>
                                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>Descrição</th>
                                        <th style={{ padding: '3px 6px', textAlign: 'right' }}>{t('nacional.suggest_carga')}</th>
                                        <th style={{ padding: '3px 6px' }}></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {nacAltMap[item.ncm].map((alt, ai) => {
                                        const isLowest = ai === 0;
                                        return (
                                          <tr key={alt.ncm} style={{
                                            borderBottom: '1px solid #FDE68A',
                                            background: isLowest ? '#F0FFF4' : 'transparent',
                                          }}>
                                            <td style={{ padding: '3px 6px', fontFamily: 'monospace', fontWeight: isLowest ? 700 : 400 }}>
                                              {alt.ncm}{isLowest ? ' ★' : ''}
                                            </td>
                                            <td style={{ padding: '3px 6px', color: 'var(--gray-600)' }}>{alt.description || '—'}</td>
                                            <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, color: isLowest ? '#276749' : 'inherit' }}>
                                              {alt.carga_aprox_pct != null ? `${alt.carga_aprox_pct}%` : '—'}
                                            </td>
                                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                                              <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '1px 8px' }}
                                                disabled={!item.id || ncmSaving === item.id}
                                                title={`Aplicar ${alt.ncm} em ${item.product || 'este item'} e recalcular`}
                                                onClick={() => saveItemNcm(item.id, alt.ncm, item.ncm)}>
                                                Usar
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                                <div style={{ marginTop: 6, textAlign: 'right' }}>
                                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}
                                    onClick={() => setNacAltOpen(null)}>
                                    {t('nacional.suggest_close')}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid var(--gray-300)', background: 'var(--gray-50)', fontWeight: 700 }}>
                        <td colSpan={2} style={{ padding: '7px 8px' }}>Total</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right' }}>{fmtBRL(totals.VA)}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right' }}>{fmtBRL(totals.ii)}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right' }}>{fmtBRL(totals.ipi)}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right' }}>{fmtBRL(totals.pis)}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right' }}>{fmtBRL(totals.cofins)}</td>
                        <td style={{ padding: '7px 8px', textAlign: 'right' }}>{fmtBRL(totals.icms)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Transformar em pedido Bulk — disponível em qualquer fase da amostra,
          inclusive depois de entregue (é aí que normalmente se fecha o bulk). */}
      {q.order_type === 'sample' && (
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-primary" onClick={() => setShowBulkModal(true)}>
            🔵 {t('bulk.open_btn')}
          </button>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--gray-400)' }}>{t('bulk.open_hint')}</div>
        </div>
      )}

      {showBulkModal && (
        <BulkOrderModal
          quotation={q}
          toast={toast}
          onClose={() => setShowBulkModal(false)}
          onCreated={result => { setShowBulkModal(false); window.location.href = `/orders/${result.id}`; }}
        />
      )}

      {(q.origin_quotation_id || q.sample_order_id) && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--gray-400)' }}>
          {q.sample_order_id ? t('bulk.origin_sample') : t('bulk.origin_quotation')}{': '}
          <a href={`/quotations/${q.origin_quotation_id || q.sample_order_id}`} style={{ color: 'var(--pantone)' }}>
            {t('bulk.origin_link')}
          </a>
        </div>
      )}

      {confirmDialog && (
        <ConfirmModal
          message={confirmDialog.message}
          onConfirm={async () => { await confirmDialog.action(); setConfirmDialog(null); }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      <ToastContainer toasts={toasts} />
    </div>
  );
}
